import * as THREE from 'three';
import { gsap } from 'gsap';
import { HeroScene } from './visualizations/HeroScene.js';
import { DimensionalScene } from './visualizations/DimensionalScene.js';
import { LandscapeScene } from './visualizations/LandscapeScene.js';
import { BusinessScene } from './visualizations/BusinessScene.js';
import { WorkforceScene } from './visualizations/WorkforceScene.js';

/**
 * Main Application
 * Handles Three.js setup, scroll-based navigation, and scene management
 *
 * Slide to Scene Mapping:
 * 0-1:   Hero (Opening)
 * 2-5:   Dimensional (1D->2D->3D vector teaching)
 * 6-7:   Landscape (AI providers)
 * 8-10:  Business (Department clusters)
 * 11-13: Workforce (Human + AI augmentation)
 * 14-15: Hero (Closing)
 */

// Standardized particle count for all scenes to enable morphing transitions
const PARTICLE_COUNT = 5000;

class App {
  constructor() {
    this.container = document.getElementById('canvas-container');
    this.slides = document.querySelectorAll('.slide');
    this.progressBar = document.getElementById('progress-bar');
    this.scrollHint = document.getElementById('scroll-hint');

    // Navigation elements
    this.prevBtn = document.getElementById('prev-btn');
    this.nextBtn = document.getElementById('next-btn');
    this.progressTrack = document.getElementById('progress-track');
    this.progressDots = document.getElementById('progress-dots');
    this.currentNum = document.getElementById('current-num');
    this.totalNum = document.getElementById('total-num');

    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.visitedSlides = new Set([0]);
    this.scrollProgress = 0;

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.clock = new THREE.Clock();

    this.mouse = new THREE.Vector2();
    this.visualizations = new Map();
    this.currentVisualization = null;
    this.isTransitioning = false;

    this.init();
  }

  init() {
    // Always start at the top of the page
    window.scrollTo(0, 0);
    this.currentSlide = 0;

    this.setupThree();
    this.setupVisualizations();
    this.setupScrollHandler();
    this.setupMouseHandler();
    this.setupResizeHandler();
    this.setupNavigation();
    this.animate();

    // Activate first slide and update visualization
    this.activateSlide(0);
    // Use requestAnimationFrame to ensure scroll position is set before updating visualization
    requestAnimationFrame(() => {
      this.updateVisualization(0, 0);
    });
  }

  setupThree() {
    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = 80;

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x0a0a0f, 1);

    this.container.appendChild(this.renderer.domElement);
  }

  setupVisualizations() {
    // Hero Scene (Closing only now - slides 14-15)
    const heroScene = new HeroScene(PARTICLE_COUNT);
    this.visualizations.set('hero', heroScene);
    heroScene.getMesh().visible = false;
    this.scene.add(heroScene.getMesh());

    // Dimensional Scene (Vector teaching - random->1D->2D->3D)
    // Used for slides 0-5 as unified particle system
    const dimensionalScene = new DimensionalScene(PARTICLE_COUNT);
    this.visualizations.set('dimensional', dimensionalScene);
    const dimMesh = dimensionalScene.getMesh();
    // Start visible (it's our opening scene now)
    this.scene.add(dimMesh);

    // AI Landscape Scene (Provider clusters)
    const landscapeScene = new LandscapeScene(PARTICLE_COUNT);
    this.visualizations.set('landscape', landscapeScene);
    landscapeScene.getMesh().visible = false;
    this.scene.add(landscapeScene.getMesh());

    // Business Scene (Department clusters)
    const businessScene = new BusinessScene(PARTICLE_COUNT);
    this.visualizations.set('business', businessScene);
    businessScene.getMesh().visible = false;
    this.scene.add(businessScene.getMesh());

    // Workforce Scene (Human + AI augmentation)
    const workforceScene = new WorkforceScene(PARTICLE_COUNT);
    this.visualizations.set('workforce', workforceScene);
    workforceScene.getMesh().visible = false;
    this.scene.add(workforceScene.getMesh());

    // Start with dimensional (unified particle system for slides 0-5)
    this.currentVisualization = dimensionalScene;
    dimensionalScene.getMesh().visible = true;
  }

  setupScrollHandler() {
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.onScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Keyboard navigation
    window.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        this.navigateToSlide(this.currentSlide + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateToSlide(this.currentSlide - 1);
      }
    });
  }

  onScroll() {
    const scrollTop = window.scrollY;
    const docHeight = document.body.scrollHeight - window.innerHeight;
    this.scrollProgress = docHeight > 0 ? scrollTop / docHeight : 0;

    // Update progress bar
    this.progressBar.style.height = `${this.scrollProgress * 100}%`;

    // Hide scroll hint after scrolling
    if (scrollTop > 100) {
      this.scrollHint.classList.add('hidden');
    }

    // Determine current slide
    const slideHeight = window.innerHeight;
    const newSlide = Math.min(Math.round(scrollTop / slideHeight), this.totalSlides - 1);

    if (newSlide !== this.currentSlide && newSlide >= 0) {
      this.activateSlide(newSlide);
    }

    // Update visualization
    this.updateVisualization(newSlide, scrollTop / slideHeight);
  }

  navigateToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;

    const targetScroll = index * window.innerHeight;
    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  }

  activateSlide(index) {
    this.slides[this.currentSlide]?.classList.remove('active');
    this.currentSlide = index;
    this.slides[this.currentSlide]?.classList.add('active');

    // Track visited slides
    this.visitedSlides.add(index);

    // Update navigation UI
    this.updateNavButtons();
    this.updateProgressUI();
  }

  updateVisualization(slideIndex, scrollPosition) {
    const hero = this.visualizations.get('hero');
    const dimensional = this.visualizations.get('dimensional');
    const landscape = this.visualizations.get('landscape');
    const business = this.visualizations.get('business');
    const workforce = this.visualizations.get('workforce');

    // Slides 0-5: Dimensional scene throughout (unified particles)
    // Slide 1-2 (index 0-1): All particles visible (state 0)
    // Slide 3 (index 2): Transition to 2 particles only
    // Slide 4 (index 3): 2 particles with labels (state 0.33)
    // Slide 5-6 (index 4-5): All particles visible, transition toward 2D/3D
    if (slideIndex <= 5) {
      this.transitionToVisualization(dimensional);

      let progress = 0;
      if (scrollPosition < 2) {
        // Slides 1-2: All particles visible, random cloud
        progress = 0;
      } else if (scrollPosition < 3) {
        // Slide 3: Transition to 2 particles (fade others out)
        progress = ((scrollPosition - 2) / 1) * 0.33;
      } else if (scrollPosition < 4) {
        // Slide 4: Stay at 1D line with 2 particles
        progress = 0.33;
      } else {
        // Slide 5-6: All particles visible, transition toward 2D/3D
        progress = 0.34 + ((scrollPosition - 4) / 2) * 0.66;
        progress = Math.min(1, progress);
      }
      dimensional.setDimensionState(progress);
    }
    // Slides 6-7: AI Landscape (providers)
    else if (slideIndex >= 6 && slideIndex <= 7) {
      this.transitionToVisualization(landscape);
    }
    // Slides 8-10: Business (departments)
    else if (slideIndex >= 8 && slideIndex <= 10) {
      this.transitionToVisualization(business);
    }
    // Slides 11-13: Workforce (human + AI)
    // Use continuous scroll position for smooth transitions
    else if (slideIndex >= 11 && slideIndex <= 13) {
      this.transitionToVisualization(workforce);
      const startSlide = 11;
      const endSlide = 13;
      const progress = Math.max(0, Math.min(1, (scrollPosition - startSlide) / (endSlide - startSlide)));
      workforce.setEvolutionState(progress);
    }
    // Slides 14-15: Hero (Closing)
    else {
      this.transitionToVisualization(hero);
    }
  }

  transitionToVisualization(newViz) {
    if (this.currentVisualization === newViz || this.isTransitioning) return;

    this.isTransitioning = true;

    const newPoints = newViz.getPoints();
    const newGeometry = newPoints.geometry;
    const newPositions = newGeometry.attributes.position.array;

    // Get the target positions - for DimensionalScene, use positionsRandom as base
    // For other scenes, use current positions as target
    let targetPositions;
    if (newViz.positionsRandom) {
      // DimensionalScene - use the random positions as target (state 0)
      targetPositions = new Float32Array(newViz.positionsRandom);
    } else {
      // Other scenes - store current positions as target
      targetPositions = new Float32Array(newPositions.length);
      targetPositions.set(newPositions);
    }

    // If we have a current visualization, morph from its positions
    if (this.currentVisualization) {
      const currentPoints = this.currentVisualization.getPoints();
      const currentGeometry = currentPoints.geometry;
      const currentPositions = currentGeometry.attributes.position.array;

      // Store start positions before modifying
      const startPositions = new Float32Array(currentPositions.length);
      startPositions.set(currentPositions);

      // Copy current positions to new scene to start the morph
      newPositions.set(currentPositions);
      newGeometry.attributes.position.needsUpdate = true;

      const newMesh = newViz.getMesh();
      const currentMesh = this.currentVisualization.getMesh();

      // Hide old scene immediately, show new scene with copied positions
      currentMesh.visible = false;
      newMesh.visible = true;

      // Use smooth easing matching DimensionalScene transitions
      gsap.to({ progress: 0 }, {
        progress: 1,
        duration: 1.5,
        ease: 'power2.inOut',
        onUpdate: function() {
          const t = this.targets()[0].progress;
          const easeT = t * t * (3 - 2 * t);

          for (let i = 0; i < newPositions.length; i++) {
            newPositions[i] = startPositions[i] + (targetPositions[i] - startPositions[i]) * easeT;
          }
          newGeometry.attributes.position.needsUpdate = true;
        },
        onComplete: () => {
          // Ensure final positions are exactly the target
          newPositions.set(targetPositions);
          newGeometry.attributes.position.needsUpdate = true;

          // Reset DimensionalScene state if transitioning to it
          if (newViz.setDimensionState) {
            newViz.dimensionState = -1; // Force update on next setDimensionState call
          }

          this.currentVisualization = newViz;
          this.isTransitioning = false;
        }
      });
    } else {
      // No current visualization, just show the new one
      const newMesh = newViz.getMesh();
      newMesh.visible = true;
      this.currentVisualization = newViz;
      this.isTransitioning = false;
    }
  }

  setupMouseHandler() {
    window.addEventListener('mousemove', (e) => {
      this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;

      this.visualizations.forEach(viz => {
        if (viz.updateMouse) viz.updateMouse(this.mouse.x, this.mouse.y);
      });
    });
  }

  setupResizeHandler() {
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    });
  }

  setupNavigation() {
    // Slide labels for tooltips
    const slideLabels = [
      'Title',
      'Speed Reality',
      'Finding Neighbors',
      '1D Line',
      '2D Meaning',
      '3D Picture',
      'AI Tools',
      'Power User',
      'Departments',
      'Technical Work',
      'SOPs',
      'Workforce',
      'New Skills',
      'Teaching',
      'Start Here',
      'Questions'
    ];

    // Set total number
    this.totalNum.textContent = this.totalSlides;

    // Create progress dots
    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('div');
      dot.className = 'progress-dot';
      dot.dataset.slide = i;
      dot.dataset.label = slideLabels[i] || `Slide ${i + 1}`;
      if (i === 0) dot.classList.add('active');

      dot.addEventListener('click', () => {
        this.navigateToSlide(i);
      });

      this.progressDots.appendChild(dot);
    }

    // Prev/Next button handlers
    this.prevBtn.addEventListener('click', () => {
      this.navigateToSlide(this.currentSlide - 1);
    });

    this.nextBtn.addEventListener('click', () => {
      this.navigateToSlide(this.currentSlide + 1);
    });

    // Update button states
    this.updateNavButtons();
  }

  updateNavButtons() {
    this.prevBtn.disabled = this.currentSlide === 0;
    this.nextBtn.disabled = this.currentSlide === this.totalSlides - 1;
  }

  updateProgressUI() {
    // Update track width
    const progress = (this.currentSlide / (this.totalSlides - 1)) * 100;
    this.progressTrack.style.width = `${progress}%`;

    // Update dots
    const dots = this.progressDots.querySelectorAll('.progress-dot');
    dots.forEach((dot, index) => {
      dot.classList.remove('active');
      if (index === this.currentSlide) {
        dot.classList.add('active');
      }
      if (this.visitedSlides.has(index)) {
        dot.classList.add('visited');
      }
    });

    // Update counter
    this.currentNum.textContent = this.currentSlide + 1;
  }

  animate() {
    requestAnimationFrame(() => this.animate());

    const elapsedTime = this.clock.getElapsedTime();
    const deltaTime = this.clock.getDelta();

    // Update visible visualizations
    this.visualizations.forEach(viz => {
      if (viz.getMesh().visible && viz.update) {
        viz.update(elapsedTime, deltaTime);
      }
    });

    // Subtle camera movement
    this.camera.position.x += (this.mouse.x * 5 - this.camera.position.x) * 0.02;
    this.camera.position.y += (this.mouse.y * 5 - this.camera.position.y) * 0.02;
    this.camera.lookAt(0, 0, 0);

    this.renderer.render(this.scene, this.camera);
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  new App();
});
